"use client";
import { atom, useAtom } from "jotai";
import { useCallback, useEffect, useRef } from "react";
import { toast } from "sonner";
import {
  MessageRole,
  Prompt,
  PromptMessage,
  PromptShareLink,
  Tag,
  TagType,
} from "@constructr-sdk/entities";
import { authClient } from "@constructr-sdk/auth/client";
import { trpcReactQueryApi } from "~/trpc/react";
import type {
  UsePromptDetailsParams,
  UsePromptDetailsReturn,
} from "~/modules/main/hooks/use-prompt-details/use-prompt-details.interface";

// Global state atoms - hoisted to top level to prevent recreation
const promptAtom = atom<Prompt | null>(null);
const draftTitleAtom = atom<string>("");
const draftMessagesAtom = atom<
  { id?: string; role: MessageRole; content: string }[]
>([]);
const tagsByTypeAtom = atom<{
  [TagType.CATEGORY]: Tag[];
  [TagType.USE_CASE]: Tag[];
  [TagType.ROLE]: Tag[];
  [TagType.INDUSTRY]: Tag[];
}>({
  [TagType.CATEGORY]: [],
  [TagType.USE_CASE]: [],
  [TagType.ROLE]: [],
  [TagType.INDUSTRY]: [],
});
const shareLinkAtom = atom<PromptShareLink | null>(null);
const loadErrorAtom = atom<string | null>(null);

/**
 * Parses dynamic variables from prompt messages.
 * Extracts all unique {{variable_name}} patterns.
 */
const parseVariables = (
  messages: { content: string }[],
): { name: string; type: "string" }[] => {
  const variableSet = new Set<string>();
  const regex = /\{\{([^}]+)\}\}/g;
  messages.forEach((msg) => {
    let match;
    while ((match = regex.exec(msg.content)) !== null) {
      variableSet.add(match[1]!.trim());
    }
  });
  return Array.from(variableSet).map((name) => ({ name, type: "string" }));
};

/**
 * Parses variables for each message individually.
 * Returns a map of messageId to array of variable names.
 */
const parseVariablesByMessage = (
  messages: PromptMessage[],
): Record<string, string[]> => {
  const result: Record<string, string[]> = {};
  const regex = /\{\{([^}]+)\}\}/g;
  messages.forEach((msg) => {
    const variables: string[] = [];
    let match;
    while ((match = regex.exec(msg.content)) !== null) {
      variables.push(match[1]!.trim());
    }
    result[msg.id] = variables;
  });
  return result;
};

export function usePromptDetails(
  params: UsePromptDetailsParams,
): UsePromptDetailsReturn {
  // Organization bootstrap to prevent 401 errors from AI Assistant
  const { data: activeOrg } = authClient.useActiveOrganization();
  const { data: orgs } = authClient.useListOrganizations();
  const didSetActiveRef = useRef<boolean>(false);

  useEffect(() => {
    if (didSetActiveRef.current) return;
    if (!activeOrg?.id && (orgs?.length ?? 0) > 0) {
      didSetActiveRef.current = true;
      void authClient.organization.setActive({ organizationId: orgs![0]!.id });
    }
  }, [activeOrg?.id, orgs]);

  // Global state
  const [prompt, setPrompt] = useAtom(promptAtom);
  const [draftTitle, setDraftTitleState] = useAtom(draftTitleAtom);
  const [draftMessages, setDraftMessagesState] = useAtom(draftMessagesAtom);
  const [tagsByType, setTagsByType] = useAtom(tagsByTypeAtom);
  const [shareLink, setShareLink] = useAtom(shareLinkAtom);
  const [loadError, setLoadError] = useAtom(loadErrorAtom);

  // TRPC utils for cache invalidation
  const utils = trpcReactQueryApi.useUtils();

  // Query for tags (loaded once)
  const {
    data: tagsData,
    isLoading: isLoadingTags,
    isError: isTagsError,
  } = trpcReactQueryApi.main.tags.allByType.useQuery(undefined);

  // Update tags when data is available
  useEffect(() => {
    if (tagsData) {
      setTagsByType(tagsData);
    }
  }, [tagsData, setTagsByType]);

  // Mutations
  const updatePromptMutation =
    trpcReactQueryApi.main.prompts.update.useMutation({
      onSuccess: async (updatedPrompt) => {
        toast.success("Prompt saved successfully");
        setPrompt(updatedPrompt);
        // Reset draft to match saved state
        setDraftTitleState(updatedPrompt.title);
        setDraftMessagesState(
          updatedPrompt.messages.map((msg) => ({
            id: msg.id,
            role: msg.role,
            content: msg.content,
          })),
        );
        await utils.main.prompts.byId.invalidate();
        await utils.main.prompts.all.invalidate();
      },
      onError: (error) => {
        toast.error(`Failed to save prompt: ${error.message}`);
      },
    });

  const publishPromptMutation =
    trpcReactQueryApi.main.prompts.publish.useMutation({
      onSuccess: async (publishedPrompt) => {
        toast.success("Prompt published successfully");
        setPrompt(publishedPrompt);
        await utils.main.prompts.byId.invalidate();
        await utils.main.prompts.all.invalidate();
      },
      onError: (error) => {
        toast.error(`Failed to publish prompt: ${error.message}`);
      },
    });

  const generateShareLinkMutation =
    trpcReactQueryApi.main.prompts.generateShareLink.useMutation({
      onSuccess: (link) => {
        toast.success("Share link generated successfully");
        setShareLink(link);
      },
      onError: (error) => {
        toast.error(`Failed to generate share link: ${error.message}`);
      },
    });

  // Computed state
  const parsedVariables =
    prompt?.messages ? parseVariables(prompt.messages) : [];
  const variablesByMessageId = prompt?.messages
    ? parseVariablesByMessage(prompt.messages)
    : {};
  const isLoading = isLoadingTags;

  // Mutations (synchronous client-side state updates)
  const setDraftTitle = useCallback(
    (title: string) => {
      setDraftTitleState(title);
    },
    [setDraftTitleState],
  );

  const setDraftMessage = useCallback(
    (messageId: string, role: MessageRole, content: string) => {
      setDraftMessagesState((prev) => {
        return prev.map((msg) =>
          msg.id === messageId ? { ...msg, role, content } : msg,
        );
      });
    },
    [setDraftMessagesState],
  );

  const addDraftMessage = useCallback(
    (role: MessageRole, content: string) => {
      setDraftMessagesState((prev) => [...prev, { role, content }]);
    },
    [setDraftMessagesState],
  );

  const removeDraftMessage = useCallback(
    (messageId: string) => {
      setDraftMessagesState((prev) => prev.filter((msg) => msg.id !== messageId));
    },
    [setDraftMessagesState],
  );

  const resetDraft = useCallback(() => {
    if (prompt) {
      setDraftTitleState(prompt.title);
      setDraftMessagesState(
        prompt.messages.map((msg) => ({
          id: msg.id,
          role: msg.role,
          content: msg.content,
        })),
      );
    }
  }, [prompt, setDraftTitleState, setDraftMessagesState]);

  // Commands (async operations with TRPC)
  const initialize = useCallback(
    async (promptId: string) => {
      try {
        setLoadError(null);
        const promptData = await utils.main.prompts.byId.fetch({ id: promptId });
        setPrompt(promptData);
        setDraftTitleState(promptData.title);
        setDraftMessagesState(
          promptData.messages.map((msg) => ({
            id: msg.id,
            role: msg.role,
            content: msg.content,
          })),
        );
        // Check if a share link exists for this prompt
        // Note: This would require a separate endpoint to fetch existing share links
        // For now, we'll set it to null and generate on demand
        setShareLink(null);
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to load prompt";
        setLoadError(errorMessage);
        toast.error(errorMessage);
      }
    },
    [
      utils.main.prompts.byId,
      setPrompt,
      setDraftTitleState,
      setDraftMessagesState,
      setShareLink,
      setLoadError,
    ],
  );

  const saveEdits = useCallback(
    async (
      promptId: string,
      title: string,
      messages: { id?: string; role: MessageRole; content: string }[],
    ) => {
      // Validate at least one message exists and title is non-empty
      if (messages.length === 0) {
        toast.error("At least one message is required");
        return;
      }
      if (title.trim().length === 0) {
        toast.error("Title cannot be empty");
        return;
      }
      await updatePromptMutation.mutateAsync({
        promptId,
        title,
        messages,
      });
    },
    [updatePromptMutation],
  );

  const publishPrompt = useCallback(
    async (
      promptId: string,
      categoryTagId: string,
      useCaseTagIds: string[],
      roleTagId: string,
      industryTagIds: string[],
    ) => {
      // Validate required tags
      if (!categoryTagId) {
        toast.error("Category tag is required");
        return;
      }
      if (useCaseTagIds.length === 0) {
        toast.error("At least one use case tag is required");
        return;
      }
      if (!roleTagId) {
        toast.error("Role tag is required");
        return;
      }
      await publishPromptMutation.mutateAsync({
        promptId,
        categoryTagId,
        useCaseTagIds,
        roleTagId,
        industryTagIds,
      });
    },
    [publishPromptMutation],
  );

  const generateShareLink = useCallback(
    async (promptId: string): Promise<PromptShareLink> => {
      const link = await generateShareLinkMutation.mutateAsync({ promptId });
      return link;
    },
    [generateShareLinkMutation],
  );

  return {
    // State
    prompt,
    parsedVariables,
    variablesByMessageId,
    tagsByType,
    isLoading,
    loadError: loadError ?? (isTagsError ? "Failed to load tags" : null),
    shareLink,

    // Mutations
    setDraftTitle,
    setDraftMessage,
    addDraftMessage,
    removeDraftMessage,
    resetDraft,

    // Commands
    initialize,
    saveEdits,
    publishPrompt,
    generateShareLink,
  };
}